import React, {useState} from 'react';
import Button from '@/components/ui/Button';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import Icon from '@/components/icons/Icon';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface EventAgent {
    fullName: string;
}

interface EventHeaderProps {
    eventType: string;
    eventId: string;
    channelName?: string;
    dateTime?: string;
    eventAgent?: EventAgent | string;
    eventCustomer?: string;
    restrictedFields?: string[];
}

const EventHeader: React.FC<EventHeaderProps> = ({eventType, eventId, channelName, dateTime, eventAgent = '', eventCustomer = '', restrictedFields = []}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [linkCopySuccess, setLinkCopySuccess] = useState<boolean>(false);

    const copyEventLink = (): void => {
        const url = `${window.location.origin}/app/events/event/${eventId}`;
        navigator.clipboard.writeText(url).then(() => {
            setLinkCopySuccess(true);
            setTimeout(() => setLinkCopySuccess(false), 2000);
        }).catch(() => {
        });
    }

    const eventIcon = (): JSX.Element | null => {
        if (eventType === 'call') {
            return <Icon name="mdi-phone" />
        } else if (eventType === 'chat') {
            return <Icon name="mdi-comment-outline" />
        } else if (eventType === 'email') {
            return <Icon name="mdi-email-outline" />
        } else if (eventType === 'feedback') {
            return <Icon name="mdi-comment-quote-outline" />
        }
        return null;
    }

    const headerText = (): JSX.Element => {
        const channelTitle = channelName?.length ? <>{channelName}</> : null

        const agentRestricted = isFieldRestricted('agent_first_name', restrictedFields);
        const customerRestricted = isFieldRestricted('customer_first_name', restrictedFields);

        const agentElement = agentRestricted
            ? <RestrictedFieldIndicator />
            : eventAgent ? (typeof eventAgent === 'object' ? <>{eventAgent.fullName}</> : <>{eventAgent}</>) : null;

        const customerElement = customerRestricted
            ? <RestrictedFieldIndicator />
            : eventCustomer ? <>{eventCustomer}</> : null;

        const eventAgentCustomerTitle = agentElement && customerElement ? (
            <>{agentElement}<Icon name="mdi-arrow-left-right" className="mx-1" />{customerElement}</>
        ) : agentElement ? agentElement : customerElement ? customerElement : null

        const eventDateTime = dateTime?.length ? <>{configLoaded ? formatDateTime(dateTime, false, {zone: 'utc'}) : ''}</> : null
        return (<>{channelTitle} | {eventAgentCustomerTitle} | {eventDateTime}</>)
    }

    return (
        <div className="d-flex py-0 w-100 align-items-center justify-content-between">
            <div className="d-flex align-items-center">
                {eventIcon()}
                <span className='ml-2 mr-4'>
                    {headerText()}
                </span>
            </div>
            <div className="d-flex align-items-center">
                <span className="text-nowrap">
                    {i18n.t('es_copy_event_id.text', {
                        eventId: eventId
                    })}
                </span>
                <Button
                    variant="link"
                    className="p-0 text-decoration-none shadow-none text-secondary"
                    onClick={() => copyEventLink()}
                    data-cy="copy-eventid-btn"
                    aria-label={i18n.t('es_copy_event.text')}
                >
                    {linkCopySuccess ? (
                        <Icon name="mdi-check" size="xs" className="text-info ml-1" />
                    ) : (
                        <Icon name="mdi-content-copy" size="xs" className="ml-1" />
                    )}
                </Button>
            </div>
        </div>
    )
}

export default EventHeader;